export const GOLDEN_ANGLE = 137.508;
export const MIN_HUE_DISTANCE = 5;

const PALETTE_SIZE = 30;
const PALETTE_STEP = 7;

const VARIANTS = [
  { s: 82, l: 44 },
  { s: 76, l: 40 },
  { s: 86, l: 48 },
] as const;

export const hashStringToUint32 = (input: string): number => {
  let hash = 0x811c9dc5;

  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash =
      (hash +
        (hash << 1) +
        (hash << 4) +
        (hash << 7) +
        (hash << 8) +
        (hash << 24)) >>>
      0;
  }

  return hash >>> 0;
};

export const hueDistance = (a: number, b: number): number => {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
};

const hsl = (h: number, s: number, l: number): string =>
  `hsl(${((h % 360) + 360) % 360}, ${s}%, ${l}%)`;

const parseHue = (color: string): number | null => {
  const m = /^hsl\(\s*([\d.]+)/i.exec(color);
  if (!m) return null;

  const h = Number(m[1]);
  return Number.isFinite(h) ? h : null;
};

const canUseHue = (h: number, used: number[], minDist: number): boolean => {
  for (let i = 0; i < used.length; i++) {
    if (hueDistance(h, used[i]) < minDist) return false;
  }
  return true;
};

const tunedVariant = (h: number, base: { s: number; l: number }) => {
  if (h >= 45 && h <= 75)
    return { s: Math.min(92, base.s + 6), l: Math.max(34, base.l - 10) };
  return base;
};

const paletteHue = (index: number) => (index * (360 / PALETTE_SIZE)) % 360;

const pickFromPalette = (
  seed: number,
  usedColors: Set<string>,
  usedHues: number[]
): { color: string; hue: number } | null => {
  const start = seed % PALETTE_SIZE;

  for (let i = 0; i < PALETTE_SIZE; i++) {
    const idx = (start + i * PALETTE_STEP) % PALETTE_SIZE;
    const hue = paletteHue(idx);

    if (!canUseHue(hue, usedHues, MIN_HUE_DISTANCE)) continue;

    const v = tunedVariant(hue, VARIANTS[0]);
    const color = hsl(Math.round(hue), v.s, v.l);

    if (!usedColors.has(color)) return { color, hue };
  }

  return null;
};

const generateBeyondPalette = (
  seed: number,
  usedColors: Set<string>,
  usedHues: number[]
): { color: string; hue: number } => {
  const baseHue = seed % 360;

  const maxDistinct = Math.floor(360 / MIN_HUE_DISTANCE); // ~72
  const minDist = usedHues.length < maxDistinct ? MIN_HUE_DISTANCE : 0;

  for (let attempt = 0; attempt < 360 * VARIANTS.length; attempt++) {
    const hue = (baseHue + attempt * GOLDEN_ANGLE) % 360;

    if (minDist > 0 && !canUseHue(hue, usedHues, minDist)) continue;

    const baseVar = VARIANTS[attempt % VARIANTS.length];
    const v = tunedVariant(hue, baseVar);
    const color = hsl(Math.round(hue), v.s, v.l);

    if (usedColors.has(color)) continue;

    return { color, hue };
  }

  const v = tunedVariant(baseHue, VARIANTS[0]);
  return { color: hsl(Math.round(baseHue), v.s, v.l), hue: baseHue };
};

export const buildCategoryColorMap = <T extends { id: string }>(
  items: T[],
  prevMap: Record<string, string>
): Record<string, string> => {
  const nextMap: Record<string, string> = {};
  const usedColors = new Set<string>();
  const usedHues: number[] = [];

  for (const item of items) {
    const existing = prevMap[item.id];
    if (!existing) continue;

    nextMap[item.id] = existing;
    usedColors.add(existing);

    const h = parseHue(existing);
    if (h !== null) usedHues.push(h);
  }

  const newItems = items
    .filter((i) => !nextMap[i.id])
    .slice()
    .sort((a, b) => a.id.localeCompare(b.id));

  for (const item of newItems) {
    const seed = hashStringToUint32(item.id);

    const fromPalette = pickFromPalette(seed, usedColors, usedHues);
    const picked =
      fromPalette ?? generateBeyondPalette(seed, usedColors, usedHues);

    nextMap[item.id] = picked.color;
    usedColors.add(picked.color);
    usedHues.push(picked.hue);
  }

  return nextMap;
};
